import { IAgeGateConfig } from '../interface/IAgeGate';
import { makeEl, addClass, getCookie, setCookie, makeElement } from '../common/Utils';

/**
 * @category ageGate
 * @description
 * NCA BNS Age Gate .js
 * 17세 연령 체크
 */

export default class AgeGate {
  private service: string;
  private serverDate: Date;
  private requiredAge: number;
  private zIndex: number;

  private readonly messages = {
    invalid: 'Sorry, your entry was invalid.',
  };

  private notVerifiedTemplate(): HTMLElement {
    return makeElement(`
      <div class="age-gate__form not-verified">
        <div class="age-gate__notice">Sorry, you do not meet the minimum requirements.</div>
      </div>
    `);
  }

  constructor(_config: IAgeGateConfig) {
    this.service = _config.service || 'bnsncwna';
    this.serverDate = _config.serverDate ? new Date(_config.serverDate) : window.userData?.now ? new Date(window.userData.now) : new Date();
    this.requiredAge = _config.requiredAge || 17;
    this.zIndex = _config.zIndex || 999999;

    const cookie = getCookie('age_verified');
    if (cookie === 'true') return;

    this.render(cookie);
  }

  private render(cookie: string | null) {
    const body = document.body;
    addClass(body, 'is-fixed');

    const wrapper = makeEl('div', body, {
      class: `ncui-ageGate is-active ${this.service}`,
      style: `z-index:${this.zIndex};`,
    });

    let innerHTML = `
      <div class="age-gate__content">
        <div class="age-gate__logo"></div>
    `;

    if (cookie === 'false') {
      innerHTML += this.notVerifiedTemplate().outerHTML;
    } else {
      innerHTML += `
        <div class="age-gate__form">
          <div class="age-gate__title">Enter your Date of Birth</div>
          <input type="text" pattern="\d*" class="age-gate__input-month" placeholder="MM" maxlength="2" min="1" max="12">
          <input type="text" pattern="\d*" class="age-gate__input-year" placeholder="YYYY" maxlength="4" min="1000">
          <div class="age-gate__alert"></div>
          <button class="age-gate__button" disabled>Submit</button>
        </div>
      `;
    }
    innerHTML += `</div></div>`;
    wrapper.appendChild(makeElement(innerHTML));

    if (!cookie) {
      this.bindEvents(wrapper);
    }
  }

  private bindEvents(wrapper: HTMLElement) {
    const monthInput = wrapper.querySelector<HTMLInputElement>('.age-gate__input-month');
    const yearInput = wrapper.querySelector<HTMLInputElement>('.age-gate__input-year');
    const submitButton = wrapper.querySelector<HTMLButtonElement>('.age-gate__button');
    const alertMessage = wrapper.querySelector<HTMLElement>('.age-gate__alert');
  
    if (!monthInput || !yearInput || !submitButton || !alertMessage) return;
  
    const parseInput = (input: HTMLInputElement, maxLength: number): number => {
      const formattedValue = input.value.replace(/[^\d]/g, '').slice(0, maxLength);
      input.value = formattedValue;
      return parseInt(formattedValue, 10);
    };
  
    const isValidDate = (month: number, year: number): boolean => {
      const currentYear = this.serverDate.getFullYear();
      return month >= 1 && month <= 12 && year < currentYear && year > currentYear - 200;
    };
  
    const validateInput = () => {
      const validMonthValue = parseInput(monthInput, 2);
      const validYearValue = parseInput(yearInput, 4);
  
      if ((monthInput.value.length === 1 || monthInput.value.length === 2) && yearInput.value.length === 4) {
        submitButton.disabled = false;
      } else {
        submitButton.disabled = true;
      }
  
      if (monthInput.value.length === 2 && document.activeElement === monthInput) {
        yearInput.focus();
        return;
      }
      alertMessage.textContent = '';
    };
  
    const handleSubmit = () => {
      const month = parseInt(monthInput.value, 10);
      const year = parseInt(yearInput.value, 10);
  
      if (!isValidDate(month, year)) {
        alertMessage.textContent = this.messages.invalid;
        return;
      }
  
      const getDate = new Date(year, month - 1, 1);
      const monthsOfDate = (this.serverDate.getFullYear() - getDate.getFullYear()) * 12 + (this.serverDate.getMonth() - getDate.getMonth());
  
      if (monthsOfDate >= this.requiredAge * 12) {
        setCookie('age_verified', 'true', 180);
        document.body.classList.remove('is-fixed');
        wrapper?.remove();
      } else {
        setCookie('age_verified', 'false', 3);
        const formEl = wrapper.querySelector('.age-gate__form');
        formEl?.remove();
        const contentWrapper = wrapper.querySelector('.age-gate__content');
        contentWrapper?.appendChild(this.notVerifiedTemplate());
      }
    };
  
    monthInput.addEventListener('input', validateInput);
    yearInput.addEventListener('input', validateInput);
  
    yearInput.addEventListener('keypress', (e) => {
      if (e.key === 'Enter' && !submitButton.disabled) {
        handleSubmit();
      }
    });
  
    submitButton.addEventListener('click', handleSubmit);
  }
  
}
